/**
 * FLIP-542: the free-trial / launch-offer numbers and copy shown on the start-trial screen, in one
 * place. The prices here are display text only - what is actually charged comes from the Stripe
 * Price / App Store product, not from this file.
 */
export const TRIAL_OFFER = {
  trialDays: 14,
  // The Apple (iOS Australia) offer code only. The Stripe route applies its discount on the server
  // and never shows a code.
  launchCode: "LAUNCHPARTY",
  // The Australian figures. Screens that can reach US users take their prices from offerPrices(region).
  launchPrice: "$12 AUD",
  launchMonths: 3,
  standardPrice: "$24 AUD",
  standardPriceNoCurrency: "$24",
  discountPercent: 50,
  planId: "digital_monthly" as const,
};

/** Where the user is priced: Australia (AUD) or the United States (USD). */
export type OfferRegion = "au" | "us";

export interface OfferPrices {
  /** The currency code sent with the checkout request, so Stripe charges what the screen showed. */
  currency: "aud" | "usd";
  launchPrice: string;
  standardPrice: string;
  standardPriceNoCurrency: string;
}

// FLIP-537. 50% off for the launch months on each currency's Stripe Price: AUD 24 -> 12, USD 16 -> 8.
const OFFER_PRICES: Record<OfferRegion, OfferPrices> = {
  au: { currency: "aud", launchPrice: "$12 AUD", standardPrice: "$24 AUD", standardPriceNoCurrency: "$24" },
  us: { currency: "usd", launchPrice: "$8 USD", standardPrice: "$16 USD", standardPriceNoCurrency: "$16" },
};

export function offerPrices(region: OfferRegion): OfferPrices {
  return OFFER_PRICES[region] ?? OFFER_PRICES.au;
}

/**
 * The region a user is priced in. An iPhone's App Store storefront is the strongest signal; without
 * one (Android, browsers) the country of the device's timezone is used. Only the United States is
 * priced in USD - every other country, and anything unknown, is Australian dollars, which is also
 * what Stripe falls back to for a country with no currency option.
 */
export function resolveOfferRegion(storefront: string | null, timezoneCountry: string | null): OfferRegion {
  const country = ((storefront || timezoneCountry) ?? "").toUpperCase();
  return country === "US" ? "us" : "au";
}

export type TrialVariant = "standard" | "ios-au";

/**
 * Which version of the start-trial screen applies. Only iOS with an Australian storefront
 * gets the launch-code flow (Apple can't apply the discount automatically); Android, Stripe
 * (web) and every other storefront get the standard one, where the discount is automatic.
 */
export function resolveTrialVariant(platform: string | null, storefront: string | null): TrialVariant {
  return platform === "iOS" && (storefront || "").toUpperCase() === "AU" ? "ios-au" : "standard";
}
